import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useTermsAcceptance } from '../../contexts/TermsAcceptanceContext';
import TourBubble from './TourBubble';

// Protected routes where the tour is relevant
const PROTECTED_PATHS = [
  '/dashboard', '/clients', '/employees', '/shifts', '/settings',
  '/warehouse', '/todo', '/messages', '/new-appointment',
  '/pending-approvals', '/subscription',
];

const OnboardingTourOverlay = () => {
  const { step, startTour, skipTour, deferTour, nextStep, completeTour } = useOnboarding();
  const { showModal: termsModalOpen } = useTermsAcceptance();
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-dismiss the 'done' step after 6 seconds
  useEffect(() => {
    if (step === 'done') {
      const t = setTimeout(() => completeTour(), 6000);
      return () => clearTimeout(t);
    }
  }, [step, completeTour]);

  // Don't render while terms acceptance is pending
  if (termsModalOpen) return null;

  // Only render on protected pages
  const onProtectedPage = PROTECTED_PATHS.some(p => location.pathname.startsWith(p));
  if (!onProtectedPage) return null;

  // Don't render if tour is inactive
  if (!step || step === 'skipped' || step === 'completed' || step === 'deferred') return null;

  // ── Initial "Need a tour?" prompt ──────────────────────────────────────────
  if (step === 'prompt') {
    return (
      <div className="fixed bottom-8 right-8 z-[190]">
        <TourBubble
          icon="👋"
          title="Καλώς ήρθατε στο TailClinic!"
          message="Θέλετε να κάνουμε μια σύντομη ξενάγηση για να ρυθμίσετε τον λογαριασμό σας;"
          actions={[
            { label: 'Ναι, πάμε!', onClick: startTour, variant: 'primary' },
            { label: 'Αργότερα', onClick: deferTour, variant: 'secondary' },
            { label: 'Όχι', onClick: skipTour, variant: 'ghost' },
          ]}
        />
      </div>
    );
  }

  // ── "Go to Settings" hint (shown on any page except /settings) ─────────────
  if (step === 'settings-intro' && location.pathname !== '/settings') {
    return (
      <div className="fixed bottom-8 right-8 z-[190] animate-fade-in">
        <TourBubble
          icon="⚙️"
          stepLabel="Βήμα 1 από 3"
          title="Ρύθμιση κλινικής"
          message="Πηγαίνετε στις Ρυθμίσεις για να ορίσετε ωράριο, κρατήσεις και online παρουσία της κλινικής."
          actions={[
            {
              label: 'Πηγαίνω στις Ρυθμίσεις →',
              onClick: () => navigate('/settings'),
              variant: 'primary',
            },
          ]}
          arrowSide={null}
        />
      </div>
    );
  }

  // ── "Go to Shifts" hint (shown on any page except /shifts) ─────────────────
  if (step === 'shifts-intro' && location.pathname !== '/shifts') {
    return (
      <div className="fixed bottom-8 right-8 z-[190] animate-fade-in">
        <TourBubble
          icon="🕐"
          stepLabel="Βήμα 2 από 3"
          title="Ανάθεση βαρδιών"
          message="Τέλεια! Τώρα πηγαίνετε στις Βάρδιες για να αναθέσετε κτηνίατρους στις βάρδιες σας."
          actions={[
            {
              label: 'Πηγαίνω στις Βάρδιες →',
              onClick: () => navigate('/shifts'),
              variant: 'primary',
            },
          ]}
          arrowSide={null}
        />
      </div>
    );
  }

  // ── Completion bubble ───────────────────────────────────────────────────────
  if (step === 'done') {
    return (
      <div className="fixed bottom-8 right-8 z-[190] animate-fade-in">
        <TourBubble
          icon="🎉"
          stepLabel="Ολοκλήρωση!"
          title="Είστε έτοιμοι!"
          message="Εξαιρετικά! Έχετε ρυθμίσει τον λογαριασμό σας και είστε πλέον έτοιμοι να χρησιμοποιήσετε το TailClinic."
          actions={[
            { label: 'Ευχαριστώ!', onClick: completeTour, variant: 'primary' },
          ]}
          arrowSide={null}
          style={{ maxWidth: '340px' }}
        />
      </div>
    );
  }

  return null;
};

export default OnboardingTourOverlay;
